import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createActivity,
  deleteActivity,
  createActivityByManager,
  createTimeEntries,
  createTimeEntryByManmager,
  deleteTimEntryById,
  endTime,
  getAllActiveTimeEntries,
  getSplitTimEntriesById,
  getTimEntryById,
  startTime,
  updateActivity,
  updateActivityByManager,
  updateTicketDetailsfromTimeEntries,
  updateTimeEntries,
  updateTimeEntryByManager,
} from "controller/api/service-desk/time-entries";
import {
  ApiErrorType,
  CreateActivityInputType,
  DeleteTimeEntryInput,
  GetSplitTimeEntriesByIdInput,
  GetTimeEntryByIdInput,
  UpdateActivityByManagerInputType,
  UpdateActivityInputType,
  UpdatetimeEntriesInput,
  createTimeEntriesInput,
  endTimerInput,
  startTimerInput,
  ticketDetailsFromTimeEntriesInput,
} from "shared";
import { sentryLogger } from "utilz/logger/sentryLogger";

export const useStartTimer = () => {
  const queryClient = useQueryClient();
  return useMutation((value: startTimerInput) => startTime(value), {
    onSuccess(data, variables, context) {
      queryClient.invalidateQueries([
        variables.serviceType,
        variables.serviceId,
      ]);
      queryClient.invalidateQueries(["timeEntries"]);
    },
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "timeEntry",
        functionName: "startTime",
      });
    },
  });
};

export const useEndTimer = () => {
  const queryClient = useQueryClient();
  return useMutation((value: endTimerInput) => endTime(value), {
    onSuccess(data, variables, context) {
      queryClient.invalidateQueries([variables.serviceType]);
      queryClient.invalidateQueries(["contracts"]);
      queryClient.invalidateQueries(["readyToBill"]);
    },

    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "timeEntry",
        functionName: "endTime",
      });
    },
  });
};

export const useCreateTimeEntry = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (value: createTimeEntriesInput) => createTimeEntries(value),
    {
      onSuccess(data, variables, context) {
        setTimeout(() => {
          queryClient.invalidateQueries([
            variables.serviceType,
            variables.serviceId,
          ]);
          queryClient.invalidateQueries(["timesheet"]);
          queryClient.invalidateQueries(["contracts"]);
          queryClient.invalidateQueries(["readyToBill"]);
        }, 3500);
      },
      onError: (error: ApiErrorType) => {
        sentryLogger({
          error: error,
          workflow: "timeEntry",
          functionName: "createTimeEntries",
        });
      },
    }
  );
};

export const useCreateTimeEntryByManager = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (value: createTimeEntriesInput) => createTimeEntryByManmager(value),
    {
      onSuccess(data, variables, context) {
        setTimeout(() => {
          queryClient.invalidateQueries([
            variables.serviceType,
            variables.serviceId,
          ]);
          queryClient.invalidateQueries(["timesheet"]);
          queryClient.invalidateQueries(["contracts"]);
          queryClient.invalidateQueries(["readyToBill"]);
        }, 2500);
      },
      onError: (error: ApiErrorType) => {
        sentryLogger({
          error: error,
          workflow: "timeEntry",
          functionName: "createTimeEntryByManmager",
        });
      },
    }
  );
};

export const useUpdateTimeEntry = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (value: UpdatetimeEntriesInput) => updateTimeEntries(value),
    {
      onSuccess(data, variables, context) {
        setTimeout(() => {
          queryClient.invalidateQueries([
            variables.serviceType,
            variables.serviceId,
          ]);
          queryClient.invalidateQueries(["timesheet"]);
          queryClient.invalidateQueries(["contracts"]);
          queryClient.invalidateQueries(["readyToBill"]);
        }, 2500);
      },
      onError: (error: ApiErrorType) => {
        sentryLogger({
          error: error,
          workflow: "timeEntry",
          functionName: "updateTimeEntries",
        });
      },
    }
  );
};

export const useUpdateTimeEntryByManager = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (value: UpdatetimeEntriesInput) => updateTimeEntryByManager(value),
    {
      onSuccess(data, variables, context) {
        setTimeout(() => {
          queryClient.invalidateQueries([
            variables.serviceType,
            variables.serviceId,
          ]);
          queryClient.invalidateQueries(["timesheet"]);
          queryClient.invalidateQueries(["contracts"]);
          queryClient.invalidateQueries(["readyToBill"]);
        }, 3500);
      },
      onError: (error: ApiErrorType) => {
        sentryLogger({
          error: error,
          workflow: "timeEntry",
          functionName: "updateTimeEntryByManager",
        });
      },
    }
  );
};

export const useUpdateTicketDetailsFromTimeEntries = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (value: ticketDetailsFromTimeEntriesInput) =>
      updateTicketDetailsfromTimeEntries(value),
    {
      onSuccess(data, variables, context) {
        queryClient.invalidateQueries(["ticket", variables.ticketId]);
      },
      onError: (error: ApiErrorType) => {
        sentryLogger({
          error: error,
          workflow: "timeEntry",
          functionName: "updateTicketDetailsfromTimeEntries",
        });
      },
    }
  );
};

export const useDeleteTimeEntries = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (value: DeleteTimeEntryInput) => deleteTimEntryById(value),
    {
      onSuccess(data, variables, context) {
        queryClient.invalidateQueries(["contracts"]);
        queryClient.invalidateQueries(["readyToBill"]);
        queryClient.invalidateQueries([
          variables.serviceType,
          variables.ticketId,
        ]);
      },
      onError: (error: ApiErrorType) => {
        sentryLogger({
          error: error,
          workflow: "timeEntry",
          functionName: "deleteTimEntryById",
        });
      },
    }
  );
};

export const useGetTimeEntrieByIdMutation = () => {
  return useMutation((value: GetTimeEntryByIdInput) => getTimEntryById(value), {
    onSuccess(data, variables, context) {},
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "timeEntry",
        functionName: "getTimEntryById",
      });
    },
  });
};

export const useGetSplittedTimeEntriesById = ({
  timeEntryId,
}: GetSplitTimeEntriesByIdInput) => {
  return useQuery(["timeEntries", timeEntryId], {
    queryFn: () => getSplitTimEntriesById({ timeEntryId }),
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "timeEntry",
        functionName: "getSplitTimEntriesById",
      });
    },
  });
};

export const useCreateActivity = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (values: CreateActivityInputType) => createActivity(values),
    {
      onSuccess(data, variables, context) {
        setTimeout(() => {
          queryClient.invalidateQueries(["timesheet"]);
        }, 3500);
      },
      onError: (error: ApiErrorType) => {
        sentryLogger({
          error: error,
          workflow: "timeEntry",
          functionName: "createActivity",
        });
      },
    }
  );
};

/**
 * Just enough of the timesheet payload to drop a row from it. The rest of this
 * module treats the shape as untyped, so there is no wider type to reuse.
 */
type TimesheetCache =
  | { result?: Array<{ activity_id?: string | null }> }
  | undefined;

export const useDeleteActivity = () => {
  const queryClient = useQueryClient();
  return useMutation((activityId: string) => deleteActivity(activityId), {
    onSuccess(_data, activityId) {
      // The delete lands on the webservice; the timesheet is read from the
      // time service, and nothing orders the two — a refetch at any delay can
      // still answer with the deleted rows and is_deleted: false. So drop them
      // from the cache instead, and deliberately do NOT invalidate afterwards:
      // the DELETE response is authoritative about what went, and asking the
      // time service again would only risk putting the activity back on screen.
      const withoutActivity = (cached: TimesheetCache): TimesheetCache => {
        if (!cached || !Array.isArray(cached.result)) return cached;
        return {
          ...cached,
          result: cached.result.filter(
            (row) => row?.activity_id !== activityId,
          ),
        };
      };

      // Every cached timesheet read starts with one of these two roots, and
      // neither is a prefix of the other: ["timesheet", ...] covers the period
      // view and the modal's entry list, ["timesheet_by_id", ...] a submission.
      queryClient.setQueriesData(["timesheet"], withoutActivity);
      queryClient.setQueriesData(["timesheet_by_id"], withoutActivity);
    },
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "timeEntry",
        functionName: "deleteActivity",
      });
    },
  });
};

export const useUpdateActivity = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (values: UpdateActivityInputType) => updateActivity(values),
    {
      onSuccess(data, variables, context) {
        setTimeout(() => {
          queryClient.invalidateQueries(["timesheet"]);
        }, 3500);
      },
      onError: (error: ApiErrorType) => {
        sentryLogger({
          error: error,
          workflow: "timeEntry",
          functionName: "updateActivity",
        });
      },
    }
  );
};

export const useCreateActivityByManager = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (values: CreateActivityInputType) => createActivityByManager(values),
    {
      onSuccess(data, variables, context) {
        setTimeout(() => {
          queryClient.invalidateQueries(["timesheet"]);
        }, 3500);
      },
      onError: (error: ApiErrorType) => {
        sentryLogger({
          error: error,
          workflow: "timeEntry",
          functionName: "createActivityByManager",
        });
      },
    }
  );
};

export const useUpdateActivityByManager = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (values: UpdateActivityByManagerInputType) =>
      updateActivityByManager(values),
    {
      onSuccess(data, variables, context) {
        setTimeout(() => {
          queryClient.invalidateQueries(["timesheet"]);
        }, 3500);
      },
      onError: (error: ApiErrorType) => {
        sentryLogger({
          error: error,
          workflow: "timeEntry",
          functionName: "updateActivityByManager",
        });
      },
    }
  );
};

export const useGetAllActiveTimeEntries = () => {
  return useQuery({
    queryKey: ["timeEntries"],
    queryFn: () => getAllActiveTimeEntries(),
    // Safety net for realtime timer sync: catch any MQTT event missed while the
    // tab was backgrounded / disconnected (see useTimeEntryMqtt).
    refetchOnWindowFocus: true,
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "timeEntry",
        functionName: "getAllActiveTimeEntries",
      });
    },
  });
};
